// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  TagsInput,
  Badge,
  SimpleGrid,
  Progress,
  Divider,
  Checkbox,
  Loader,
  Center,
  ThemeIcon,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useForm } from '@mantine/form';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconDownload,
  IconEdit,
  IconTrash,
  IconCheck,
  IconFileDescription,
  IconHistory,
  IconClock,
  IconUser,
  IconTag,
  IconCategory,
  IconFile,
} from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import {
  useDocument,
  useDocumentAcknowledgments,
  useDocumentVersions,
  useUpdateDocument,
  useDeleteDocument,
  useAcknowledgeDocument,
} from '../../hooks/useDocuments';
import type { DocumentCategory, DocumentAcknowledgment } from '../../types/document';

// ── Constants ───────────────────────────────────────────────────────

const DOCUMENT_CATEGORIES: { value: DocumentCategory; label: string }[] = [
  { value: 'EMPLOYEE_FILE', label: 'Employee File' },
  { value: 'TAX_FORM', label: 'Tax Form' },
  { value: 'POLICY_DOCUMENT', label: 'Policy Document' },
  { value: 'HANDBOOK', label: 'Handbook' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'CERTIFICATION', label: 'Certification' },
  { value: 'TRAINING', label: 'Training' },
  { value: 'PERFORMANCE_REVIEW', label: 'Performance Review' },
  { value: 'DISCIPLINARY', label: 'Disciplinary' },
  { value: 'BENEFITS', label: 'Benefits' },
  { value: 'ONBOARDING', label: 'Onboarding' },
  { value: 'OFFBOARDING', label: 'Offboarding' },
  { value: 'COMPLIANCE', label: 'Compliance' },
  { value: 'OTHER', label: 'Other' },
];

const CATEGORY_COLORS: Record<string, string> = {
  EMPLOYEE_FILE: 'blue',
  TAX_FORM: 'red',
  POLICY_DOCUMENT: 'violet',
  HANDBOOK: 'indigo',
  CONTRACT: 'orange',
  CERTIFICATION: 'teal',
  TRAINING: 'cyan',
  PERFORMANCE_REVIEW: 'yellow',
  DISCIPLINARY: 'pink',
  BENEFITS: 'green',
  ONBOARDING: 'lime',
  OFFBOARDING: 'gray',
  COMPLIANCE: 'grape',
  OTHER: 'dark',
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

function categoryLabel(category: string): string {
  const found = DOCUMENT_CATEGORIES.find((c) => c.value === category);
  return found?.label ?? category;
}

// ── Row types ───────────────────────────────────────────────────────

interface AckRow extends Record<string, unknown> {
  documentId: string;
  employeeId: string;
  employeeName: string;
  acknowledgedAt: string;
  ipAddress?: string;
}

interface VersionRow extends Record<string, unknown> {
  id: string;
  version: number;
  isActive: boolean;
  name: string;
  fileName: string;
  fileSize: number;
  uploadedBy: string;
  uploadedAt: string;
  createdAt: string;
}

// ── Component ───────────────────────────────────────────────────────

export default function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const { data: docData, loading } = useDocument(id || '');
  const doc = docData?.document;
  const { data: ackData } = useDocumentAcknowledgments(id || '');
  const acknowledgments = (ackData as any)?.documentAcknowledgments;
  const { data: versionsData } = useDocumentVersions(doc?.originalId || doc?.id || '');
  const versions = (versionsData as any)?.documentVersions;

  const updateDoc = useUpdateDocument();
  const deleteDoc = useDeleteDocument();
  const acknowledgeDoc = useAcknowledgeDocument();

  // ── Edit form ───────────────────────────────────────────────────

  const editForm = useForm({
    initialValues: {
      name: '',
      description: '',
      category: '',
      tags: [] as string[],
      requiresAcknowledgment: false,
    },
    validate: {
      name: (v: string) => (v && v.trim() ? null : 'Document name is required'),
      category: (v: string) => (v ? null : 'Category is required'),
    },
  });

  const openEdit = () => {
    if (!doc) return;
    editForm.setValues({
      name: doc.name,
      description: doc.description || '',
      category: doc.category,
      tags: doc.tags || [],
      requiresAcknowledgment: doc.requiresAcknowledgment,
    });
    setEditOpen(true);
  };

  const handleEditSubmit = editForm.onSubmit(async (values) => {
    if (!doc) return;
    try {
      await updateDoc.mutateAsync({
        id: doc.id,
        input: {
          name: values.name,
          category: values.category,
          description: values.description || undefined,
          tags: values.tags,
          requiresAcknowledgment: values.requiresAcknowledgment,
        },
      });
      notifications.show({
        title: 'Document Updated',
        message: 'A new version of this document metadata has been created (append-only).',
        color: 'green',
      });
      setEditOpen(false);
    } catch (err) {
      notifications.show({
        title: 'Update Failed',
        message: err instanceof Error ? err.message : 'Failed to update document',
        color: 'red',
      });
    }
  });

  // ── Delete handler ──────────────────────────────────────────────

  const handleDelete = async () => {
    if (!doc || !deleteReason.trim()) return;
    try {
      await deleteDoc.mutateAsync({ id: doc.id, reason: deleteReason });
      notifications.show({
        title: 'Document Deleted',
        message: 'Document soft-deleted. Redirecting to document list.',
        color: 'green',
      });
      navigate('/admin/workforce/documents');
    } catch (err) {
      notifications.show({
        title: 'Delete Failed',
        message: err instanceof Error ? err.message : 'Failed to delete document',
        color: 'red',
      });
    }
  };

  // ── Acknowledge handler ─────────────────────────────────────────

  const handleAcknowledge = async () => {
    if (!doc) return;
    try {
      await acknowledgeDoc.mutateAsync({ documentId: doc.id });
      notifications.show({
        title: 'Document Acknowledged',
        message: 'Your acknowledgment has been recorded.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Acknowledgment Failed',
        message: err instanceof Error ? err.message : 'Failed to acknowledge document',
        color: 'red',
      });
    }
  };

  // ── Acknowledgment table columns ────────────────────────────────

  const ackColumns: Column<AckRow>[] = [
    {
      key: 'employeeName',
      label: 'Employee',
      sortable: true,
      render: (row) => <Text size="sm" fw={500}>{row.employeeName}</Text>,
    },
    {
      key: 'acknowledgedAt',
      label: 'Acknowledged At',
      sortable: true,
      render: (row) => (
        <Text size="sm" c="dimmed">
          {new Date(row.acknowledgedAt).toLocaleString()}
        </Text>
      ),
    },
    {
      key: 'ipAddress',
      label: 'IP Address',
      width: 140,
      render: (row) => (
        <Text size="sm" c="dimmed">{row.ipAddress || '--'}</Text>
      ),
    },
  ];

  // ── Version history columns ─────────────────────────────────────

  const versionColumns: Column<VersionRow>[] = [
    {
      key: 'version',
      label: 'Version',
      width: 80,
      render: (row) => (
        <Badge variant={row.isActive ? 'filled' : 'light'} color={row.isActive ? 'blue' : 'gray'} size="sm">
          v{row.version}
        </Badge>
      ),
    },
    {
      key: 'name',
      label: 'Name',
      render: (row) => <Text size="sm">{row.name}</Text>,
    },
    {
      key: 'fileName',
      label: 'File',
      render: (row) => <Text size="sm" c="dimmed">{row.fileName}</Text>,
    },
    {
      key: 'fileSize',
      label: 'Size',
      width: 90,
      render: (row) => <Text size="sm" c="dimmed">{formatFileSize(row.fileSize)}</Text>,
    },
    {
      key: 'uploadedBy',
      label: 'Uploaded By',
      width: 120,
      render: (row) => <Text size="sm">{row.uploadedBy}</Text>,
    },
    {
      key: 'createdAt',
      label: 'Date',
      width: 150,
      render: (row) => (
        <Text size="sm" c="dimmed">{new Date(row.createdAt).toLocaleString()}</Text>
      ),
    },
  ];

  // ── Loading / Not found ─────────────────────────────────────────

  if (loading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!doc) {
    return (
      <Stack gap="md">
        <PageHeader
          title="Document Not Found"
          breadcrumbs={[
            { label: 'Documents', to: '/admin/workforce/documents' },
            { label: 'Not Found' },
          ]}
        />
        <Paper p="xl" ta="center">
          <Text c="dimmed">The requested document could not be found.</Text>
          <Button variant="light" mt="md" onClick={() => navigate('/admin/workforce/documents')}>
            Back to Documents
          </Button>
        </Paper>
      </Stack>
    );
  }

  const isActive = doc.isActive !== false;
  const acked = doc.acknowledgedCount ?? 0;
  const total = doc.totalRecipients ?? 0;
  const ackPct = total > 0 ? Math.round((acked / total) * 100) : 0;

  return (
    <Stack gap="md">
      <PageHeader
        title={doc.name}
        breadcrumbs={[
          { label: 'Documents', to: '/admin/workforce/documents' },
          { label: doc.name },
        ]}
        actions={
          <Group gap="sm">
            {doc.downloadUrl && (
              <Button
                variant="light"
                icon={<IconDownload size={16} />}
                component="a"
                href={doc.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download
              </Button>
            )}
            {isActive && (
              <>
                <Button
                  variant="light"
                  color="yellow"
                  icon={<IconEdit size={16} />}
                  onClick={openEdit}
                >
                  Edit Metadata
                </Button>
                <Button
                  variant="light"
                  color="red"
                  icon={<IconTrash size={16} />}
                  onClick={() => setDeleteOpen(true)}
                >
                  Delete
                </Button>
              </>
            )}
          </Group>
        }
      />

      {/* File info */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Paper p="md" radius="sm" withBorder>
          <Text size="sm" fw={600} mb="sm">File Information</Text>
          <Stack gap="xs">
            <Group gap="sm">
              <ThemeIcon variant="light" size="sm" color="gray">
                <IconFile size={14} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" w={100}>File Name</Text>
              <Text size="sm" fw={500}>{doc.fileName}</Text>
            </Group>
            <Group gap="sm">
              <ThemeIcon variant="light" size="sm" color="gray">
                <IconCategory size={14} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" w={100}>Category</Text>
              <Badge variant="light" color={CATEGORY_COLORS[doc.category] || 'gray'} size="sm">
                {categoryLabel(doc.category)}
              </Badge>
            </Group>
            <Group gap="sm">
              <ThemeIcon variant="light" size="sm" color="gray">
                <IconFileDescription size={14} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" w={100}>Size</Text>
              <Text size="sm">{formatFileSize(doc.fileSize)}</Text>
            </Group>
            <Group gap="sm">
              <ThemeIcon variant="light" size="sm" color="gray">
                <IconFileDescription size={14} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" w={100}>MIME Type</Text>
              <Text size="sm">{doc.mimeType}</Text>
            </Group>
            <Group gap="sm">
              <ThemeIcon variant="light" size="sm" color="gray">
                <IconHistory size={14} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" w={100}>Version</Text>
              <Badge variant="light" color="blue" size="sm">v{doc.version ?? 1}</Badge>
            </Group>
            {doc.expiresAt && (
              <Group gap="sm">
                <ThemeIcon variant="light" size="sm" color="orange">
                  <IconClock size={14} />
                </ThemeIcon>
                <Text size="sm" c="dimmed" w={100}>Expires</Text>
                <Text size="sm" c="orange">{new Date(doc.expiresAt).toLocaleDateString()}</Text>
              </Group>
            )}
          </Stack>
        </Paper>

        <Paper p="md" radius="sm" withBorder>
          <Text size="sm" fw={600} mb="sm">Upload Details</Text>
          <Stack gap="xs">
            <Group gap="sm">
              <ThemeIcon variant="light" size="sm" color="gray">
                <IconUser size={14} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" w={100}>Uploaded By</Text>
              <Text size="sm" fw={500}>{doc.uploadedBy}</Text>
            </Group>
            <Group gap="sm">
              <ThemeIcon variant="light" size="sm" color="gray">
                <IconClock size={14} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" w={100}>Upload Date</Text>
              <Text size="sm">{new Date(doc.uploadedAt).toLocaleString()}</Text>
            </Group>
            {doc.employeeName && (
              <Group gap="sm">
                <ThemeIcon variant="light" size="sm" color="gray">
                  <IconUser size={14} />
                </ThemeIcon>
                <Text size="sm" c="dimmed" w={100}>Employee</Text>
                <Text size="sm" fw={500}>{doc.employeeName}</Text>
              </Group>
            )}
            <Divider my="xs" />
            {doc.description && (
              <>
                <Text size="sm" fw={600}>Description</Text>
                <Text size="sm" c="dimmed">{doc.description}</Text>
              </>
            )}
            {doc.tags && doc.tags.length > 0 && (
              <>
                <Group gap="sm" mt="xs">
                  <ThemeIcon variant="light" size="sm" color="gray">
                    <IconTag size={14} />
                  </ThemeIcon>
                  <Text size="sm" fw={600}>Tags</Text>
                </Group>
                <Group gap={4}>
                  {doc.tags.map((tag: string) => (
                    <Badge key={tag} variant="outline" size="sm" color="gray">
                      {tag}
                    </Badge>
                  ))}
                </Group>
              </>
            )}
          </Stack>
        </Paper>
      </SimpleGrid>

      {/* Acknowledgment section */}
      {doc.requiresAcknowledgment && (
        <Paper p="md" radius="sm" withBorder>
          <Group justify="space-between" mb="md">
            <div>
              <Text size="sm" fw={600}>Acknowledgment Tracking</Text>
              <Text size="xs" c="dimmed">
                {acked} of {total} employees have acknowledged this document
              </Text>
            </div>
            <Button
              size="sm"
              icon={<IconCheck size={16} />}
              onClick={handleAcknowledge}
              loading={acknowledgeDoc.isPending}
              disabled={ackPct === 100}
            >
              Acknowledge
            </Button>
          </Group>

          <Progress
            value={ackPct}
            size="lg"
            color={ackPct === 100 ? 'green' : ackPct > 50 ? 'yellow' : 'red'}
            mb="md"
          />
          <Text size="xs" c="dimmed" ta="center" mb="md">
            {ackPct}% complete
          </Text>

          {acknowledgments && acknowledgments.length > 0 && (
            <DataTable<AckRow>
              columns={ackColumns}
              data={acknowledgments as AckRow[]}
              getRowKey={(row) => `${row.documentId}-${row.employeeId}`}
              emptyMessage="No acknowledgments yet."
            />
          )}
        </Paper>
      )}

      {/* Version history */}
      {versions && versions.length > 1 && (
        <Paper p="md" radius="sm" withBorder>
          <Group gap="sm" mb="md">
            <IconHistory size={18} color="var(--mantine-color-blue-6)" />
            <Text size="sm" fw={600}>Version History</Text>
            <Badge variant="light" size="sm">{versions.length} versions</Badge>
          </Group>

          <DataTable<VersionRow>
            columns={versionColumns}
            data={versions as VersionRow[]}
            getRowKey={(row) => row.id}
            emptyMessage="No version history available."
          />
        </Paper>
      )}

      {/* ── Edit Modal ────────────────────────────────────────────── */}
      <Modal
        opened={editOpen}
        onClose={() => {
          setEditOpen(false);
          editForm.reset();
        }}
        title="Edit Document Metadata (new version)"
        size="lg"
      >
        <form onSubmit={handleEditSubmit}>
          <Stack gap="md">
            <TextInput
              label="Document Name"
              required
              {...editForm.getInputProps('name')}
            />

            <Select
              label="Category"
              required
              data={DOCUMENT_CATEGORIES}
              {...editForm.getInputProps('category')}
            />

            <Textarea
              label="Description"
              placeholder="Brief description..."
              minRows={2}
              {...editForm.getInputProps('description')}
            />

            <TagsInput
              label="Tags"
              placeholder="Add tags..."
              data={editForm.values.tags}
              {...editForm.getInputProps('tags')}
            />

            <Checkbox
              label="Requires employee acknowledgment"
              {...editForm.getInputProps('requiresAcknowledgment', { type: 'checkbox' })}
            />

            <Paper p="sm" bg="blue.0" radius="sm">
              <Text size="xs" c="blue.7">
                Append-only: Saving will create a new version. The previous version remains immutable in the audit trail.
              </Text>
            </Paper>

            <Group justify="flex-end">
              <Button variant="default" onClick={() => { setEditOpen(false); editForm.reset(); }}>
                Cancel
              </Button>
              <Button type="submit" loading={updateDoc.isPending}>
                Save New Version
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* ── Delete Modal ──────────────────────────────────────────── */}
      <Modal
        opened={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteReason('');
        }}
        title="Delete Document"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will soft-delete{' '}
            <Text span fw={600}>{doc.name}</Text>{' '}
            by creating a new inactive version (append-only). The original file is preserved for audit.
          </Text>
          <Textarea
            label="Reason for deletion"
            placeholder="Required for audit trail..."
            required
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.currentTarget.value)}
            minRows={2}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteOpen(false);
                setDeleteReason('');
              }}
            >
              Keep Document
            </Button>
            <Button
              color="red"
              disabled={!deleteReason.trim()}
              loading={deleteDoc.isPending}
              onClick={handleDelete}
            >
              Delete Document
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
